import React, { useRef } from 'react';
import { FieldArray, FieldArrayRenderProps, Formik, FormikProps } from 'formik';
import { Modal, Row, Col, Button, Divider } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { Form } from 'formik-antd';
import * as Yup from 'yup';
import cx from 'classnames';

import BaseAlert from 'components/wrappers/BaseAlert';
import SubmitButton from 'components/formik/SubmitButton';
import { AdditionalInfoSectionEnum } from 'features/account/enums';
import ContactsSection from 'components/modals/sections/ContactsSection';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import { updateTimesheetApproverInformation } from 'redux/reducers/accountSlice';
import { updateCorporateTATimesheetApproverInformation } from 'redux/reducers/recruiterSlice';
import { ProfileCorporateTimesheetApproverInfoModel } from 'api/backofficeApiClient';
import useCanRemoveContact from 'features/account/AdditionalInfo/hooks/useCanRemoveContact';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

import styles from './EditTimesheetApproverInfoModal.module.less';

type Props = {
  onClose: () => void;
  onEdit: () => Promise<void>;
  companyId?: string;
  timesheetApproverInfo?: ProfileCorporateTimesheetApproverInfoModel[];
};

interface TimesheetApproverFormValues {
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

interface TimesheetApproverInfoFormValues {
  timesheetApprovers: Array<TimesheetApproverFormValues>;
}

const timesheetApproverValidationSchema = {
  firstName: Yup.string()
    .trim()
    .max(120, 'First name should be 120 characters maximum'),
  lastName: Yup.string()
    .trim()
    .max(120, 'Last name should be 120 characters maximum'),
  email: Yup.string()
    .email('Email format is invalid')
    .max(120, 'Email should be 120 characters maximum'),
  phone: Yup.string()
    .trim()
    .max(120, 'Phone number should be 120 characters maximum'),
};

const isRemoveTimesheetApproverButtonVisible = (
  timesheetApprovers: Array<TimesheetApproverFormValues>,
) => timesheetApprovers.length > 1;

const isAddTimesheetApproverButtonVisible = (
  timesheetApprovers: Array<TimesheetApproverFormValues>,
  index: number,
) => index === timesheetApprovers.length - 1;

const validationSchema = Yup.object({
  timesheetApprovers: Yup.array().of(
    Yup.object({
      ...timesheetApproverValidationSchema,
      firstName:
        timesheetApproverValidationSchema.firstName.required(
          'Field is required',
        ),
      lastName:
        timesheetApproverValidationSchema.lastName.required(
          'Field is required',
        ),
      email:
        timesheetApproverValidationSchema.email.required('Field is required'),
      phone:
        timesheetApproverValidationSchema.phone.required('Field is required'),
    }),
  ),
});

const TimesheetApproverFieldArray = React.memo(
  (props: FieldArrayRenderProps) => {
    const { timesheetApprovers } = (
      props.form as FormikProps<TimesheetApproverInfoFormValues>
    ).values;
    const [canRemoveOptions, checkCanRemoveContact] = useCanRemoveContact(
      AdditionalInfoSectionEnum.TimesheetApprover,
    );

    const handleRemoveContact = async (index: number) => {
      const { id } = timesheetApprovers[index];
      const canContactBeRemoved = await checkCanRemoveContact(id);

      canContactBeRemoved && removeContactFromArray(index);
    };

    const removeContactFromArray = (index: number) => {
      props.remove(index);
    };

    const isContactChecking = (timesheetApproverId?: string) => {
      const { isLoading, contactId } = canRemoveOptions;

      return isLoading && contactId === timesheetApproverId;
    };

    return (
      <>
        {timesheetApprovers.length > 0 &&
          timesheetApprovers.map(
            (timesheetApprover: TimesheetApproverFormValues, index: number) => (
              <div key={index} className={styles.timesheerApproverSection}>
                <ContactsSection
                  index={index}
                  isTitleEnabled={false}
                  formNamePrefix={props.name}
                  actionButtons={
                    <>
                      {isAddTimesheetApproverButtonVisible(
                        timesheetApprovers,
                        index,
                      ) && (
                        <Button
                          className={cx('borderless-btn', styles.btn)}
                          onClick={() =>
                            props.push({
                              firstName: '',
                              lastName: '',
                              email: '',
                              phone: '',
                            })
                          }
                          icon={<span className="icon icon-plus-filled"></span>}
                        />
                      )}

                      {isRemoveTimesheetApproverButtonVisible(
                        timesheetApprovers,
                      ) && (
                        <Button
                          disabled={canRemoveOptions.isLoading}
                          className={cx('borderless-btn', styles.btn)}
                          onClick={() => handleRemoveContact(index)}
                          icon={
                            isContactChecking(timesheetApprover.id) ? (
                              <LoadingOutlined />
                            ) : (
                              <span className="icon icon-closed-filled" />
                            )
                          }
                        />
                      )}
                    </>
                  }
                />

                <Divider className="modal-divider after-form" />
              </div>
            ),
          )}
      </>
    );
  },
  (prevProps: FieldArrayRenderProps, nextProps: FieldArrayRenderProps) => {
    return (
      (prevProps.form.values as TimesheetApproverInfoFormValues)
        .timesheetApprovers.length ===
      (nextProps.form.values as TimesheetApproverInfoFormValues)
        .timesheetApprovers.length
    );
  },
);

export default function EditTimesheetApproverInfoModal(props: Props) {
  const { companyId, timesheetApproverInfo, onClose, onEdit } = props;
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(isAdminSelector);
  const formRef: any = useRef();
  const initialValues: TimesheetApproverInfoFormValues = {
    timesheetApprovers: !!timesheetApproverInfo?.length
      ? timesheetApproverInfo.map(
          (timesheetApprover): TimesheetApproverFormValues => ({
            id: timesheetApprover.timesheetApproverId,
            firstName: timesheetApprover.firstName || '',
            lastName: timesheetApprover.lastName || '',
            email: timesheetApprover.email || '',
            phone: timesheetApprover.phoneNumber || '',
          }),
        )
      : [
          {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
          },
        ],
  };

  const handleSubmit = async (values: TimesheetApproverInfoFormValues) => {
    const { timesheetApprovers } = values;

    const timesheetApproversInfoData = timesheetApprovers.map(
      (timesheetApprover): ProfileCorporateTimesheetApproverInfoModel => ({
        timesheetApproverId: timesheetApprover.id,
        firstName: timesheetApprover.firstName || undefined,
        lastName: timesheetApprover.lastName || undefined,
        email: timesheetApprover.email || undefined,
        phoneNumber: timesheetApprover.phone,
      }),
    );

    const correctAction = isAdmin
      ? updateCorporateTATimesheetApproverInformation
      : updateTimesheetApproverInformation;
    const requestData = {
      companyId: isAdmin ? companyId : undefined,
      timesheetApproversData: {
        timesheetApprovers: timesheetApproversInfoData,
      },
    };
    const result = await dispatch(correctAction(requestData));

    if (correctAction.fulfilled.match(result)) {
      await onEdit();

      BaseAlert.success('Timesheet approver information saved.');
      onClose();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleModalCancel = () => {
    if (formRef.current?.isSubmitting) return;

    if (formRef.current?.dirty) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }
    onClose();
  };

  return (
    <BaseModal
      title="Edit timesheet approver information"
      maxWidth={888}
      open={true}
      onCancel={handleModalCancel}
    >
      <Formik
        enableReinitialize
        innerRef={formRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <Form layout="vertical">
          <div className="modal-scroll-wrapper">
            <Row gutter={48} align="middle">
              <Col xs={24} md={7}>
                <div className="modal-title">Timesheet approver</div>
              </Col>
            </Row>

            <FieldArray
              name="timesheetApprovers"
              component={TimesheetApproverFieldArray as any}
            />
          </div>

          <BaseModalFooter>
            <Button onClick={handleModalCancel}>Cancel</Button>

            <SubmitButton type="primary">Save</SubmitButton>
          </BaseModalFooter>
        </Form>
      </Formik>
    </BaseModal>
  );
}
